import { MouseEvent, TouchEvent, useCallback, useEffect, useMemo, useRef } from 'react'

import {
  EDITOR_DOUBLE_CLICK_POS_THRESHOLD,
  EDITOR_TOOLS,
  MAXIMUM_NUMBER_OF_SHAPE_DETECTION_POLYGON,
} from '@editor/config/constants'
import { DEFAULT_PLANES_STYLES, HOVERED_PLANE_STYLES, SELECTED_PLANE_STYLES } from '@editor/config/styles/plane'
import { useEditorContext } from '@editor/contexts/Editor'
import { hashString } from '@editor/hooks/useHashUpdater'
import { PointArray } from '@editor/interfaces/attribute'
import { AnchorColors, LineStyle, PolygonPlaneMeshProps } from '@editor/interfaces/canvas'
import { CanvasConfig, CanvasEventsExtra, CanvasProps } from '@editor/interfaces/editor'
import { InspectionItemType } from '@editor/interfaces/inspection'
import { Polygon } from '@editor/interfaces/shape'
import { setAttentionText } from '@editor/pages/projects/common/AttentionText/store/attentionText'
import useConfigProps from '@editor/pages/projects/editor/hooks/useConfigProps'
import { useFocusItem } from '@editor/pages/projects/editor/hooks/useFocusItem'
import useUnlabeledColor from '@editor/pages/projects/editor/hooks/useUnlabeledColor'
import { isElementHovered } from '@editor/pages/projects/editor/infoPanels/utils'
import {
  selectIsAdjustingLabelColors,
  selectIsAdjustingUnlabeledColors,
  selectisGroupColoring,
} from '@editor/pages/projects/editor/store/selectors'
import { parseShapePartitionKey } from '@editor/services/InspectionArea'
import { toInspectionGroups } from '@editor/services/InspectionGroup'
import { findCenter } from '@editor/services/Points'
import { checkForLineIntersections } from '@editor/services/Polygon'
import { alignToZAxis, generateDummyMinimumRectBoundary } from '@editor/services/Shape'
import { zeroPad } from '@editor/services/Util'
import { getVolumeEstimationItem } from '@editor/services/VolumeEstimation'
import { useAppDispatch, useAppSelector } from '@editor/store/app'
import { selectConnectedAreas, selectInspectionItems, useToolUtilSelector } from '@editor/store/selectors'
import { toaster } from '@editor/system/toaster'
import { CircleAnchorIcon } from '@infraloop/common'
import * as Sentry from '@sentry/react'
import { cloneDeep } from 'lodash'
import { Plane, Vector3 } from 'three'

import { addWorkingPoint, completePlane, resetWorkingPoints, undoLastWorkingPlane, updatePlanePoints } from '../store'
import { WORKING_STYLES, getNonSavedPlaneStyles } from '../utils'
import { useAnchors } from './useAnchors'

export default function useMainCanvas(props?: CanvasProps): CanvasConfig {
  const { setPointProjectionPlane, cameraRef } = useEditorContext()
  const { showLabels } = useConfigProps(props)
  const focusToItem = useFocusItem()

  // Toast
  const toastIdRef = useRef<string>(undefined)

  // Store
  const dispatch = useAppDispatch()
  const connectedAreas = useAppSelector(selectConnectedAreas)
  const inspectionItems = useAppSelector(selectInspectionItems)
  const planeItems = useMemo(() => inspectionItems.filterByType(InspectionItemType.PolygonArea), [inspectionItems])
  const shapes = useAppSelector((state) => state.shapes.shapes)
  const inspectionGroupsPanelItems = useAppSelector((state) => state.inspectionGroup.groups)

  const isAdjustingUnlabeledColors = useAppSelector(selectIsAdjustingUnlabeledColors)
  const isAdjustingLabelColors = useAppSelector(selectIsAdjustingLabelColors)
  const isGroupColoring = useAppSelector(selectisGroupColoring)
  const planeDisplaySettings = useAppSelector((state) => state.editorSettings.display.plane)
  const getUnlabeledColor = useUnlabeledColor()
  const currentAdjustingKey = useAppSelector((state) => state.toolUtil.core.currentAdjustingKey)

  const selectedTool = useAppSelector((state) => state.editor.selectedTool)
  const hoveredElementId = useToolUtilSelector((state) => state.hoveredElementId)
  const hiddenElementIds = useToolUtilSelector((state) => state.hiddenElementIds)
  const selectedElementIds = useToolUtilSelector((state) => state.selectedElementIds)
  const editingElementId = useToolUtilSelector((state) => state.editingElementId)

  const workingPoints = useAppSelector((root) => root.toolPlaneDetection.workingPoints)
  const planes = useAppSelector((root) => root.toolPlaneDetection.planes)
  const savingPlanes = useAppSelector((root) => root.toolPlaneDetection.savingPlanes)
  const errorPlanes = useAppSelector((root) => root.toolPlaneDetection.errorPlanes)

  // Refs
  const threePlanesRef = useRef<Record<string, Plane>>({})
  const touchedPoint = useRef<PointArray | undefined>(undefined)
  const isUpTouchedHandled = useRef(false)

  // Hooks
  const { anchors, isClosing, setIsClosing } = useAnchors()

  // Vars
  const inspectionGroups = toInspectionGroups(inspectionGroupsPanelItems)
  const isSelectedTool = selectedTool === EDITOR_TOOLS.PLANE
  const isSelectorTool = selectedTool === EDITOR_TOOLS.MOVE

  // State checks
  const isEditing = useCallback((id: string) => id === editingElementId, [editingElementId])

  useEffect(() => {
    planes.forEach((plane) => {
      threePlanesRef.current[plane.id] = new Plane().setFromCoplanarPoints(
        new Vector3(...plane.points[0]),
        new Vector3(...plane.points[1]),
        new Vector3(...plane.points[2]),
      )
    })
  }, [planes])

  /**
   * Set attention text based on the current drawing stage.
   */
  useEffect(() => {
    if (!isSelectedTool) return

    dispatch(
      setAttentionText({
        message: `平面の頂点を選択してください。同時に${MAXIMUM_NUMBER_OF_SHAPE_DETECTION_POLYGON}個まで作成することができます。\n選択が完了したら右下の測定ボタンをクリックしてください。`,
      }),
    )
  }, [isSelectedTool, dispatch])

  /**
   * Validate if adding a new point will create intersecting lines.
   *
   * @param points Current points including the new point to be added
   */
  const validateIntersectingLines = useCallback((points: PointArray[]): boolean => {
    // Need at least 3 points to have intersecting lines
    if (points.length < 3) return true

    // Check for line intersections in 2D (since points are coplanar)
    const { points: zAlignedPoints } = alignToZAxis(points.map((p) => new Vector3(...p)))
    if (checkForLineIntersections(zAlignedPoints)) {
      if (toastIdRef.current) toaster.dismiss(toastIdRef.current)
      toastIdRef.current = toaster.create({
        title: '交差検出',
        description: '新しい点により線分が交差してしまいます。別の位置を選択してください。',
        type: 'warning',
        duration: 5000,
      })
      return false
    }

    return true
  }, [])

  /**
   * Handle mouse/touch up event to add points or complete the plane.
   */
  const handleUp = useCallback(
    (
      me: MouseEvent<HTMLDivElement> | null,
      te: TouchEvent<HTMLDivElement> | null,
      point: PointArray | undefined,
      solidMeshPoint: PointArray | undefined,
      { isDoubleClicked, isDragged }: CanvasEventsExtra,
    ) => {
      const clickedPoint = solidMeshPoint || point
      if (isDragged || !isSelectedTool || !clickedPoint) {
        return
      }

      if (planes.length >= MAXIMUM_NUMBER_OF_SHAPE_DETECTION_POLYGON) {
        if (toastIdRef.current) toaster.dismiss(toastIdRef.current)
        toastIdRef.current = toaster.create({
          title: `同時に検出できる要素は${MAXIMUM_NUMBER_OF_SHAPE_DETECTION_POLYGON}個までです。`,
          description: `右側の「検出中の要素」パネルから削除して${MAXIMUM_NUMBER_OF_SHAPE_DETECTION_POLYGON}個以下にしてください。`,
          type: 'warning',
          duration: Infinity,
        })
        return
      }

      // Figure out distance in screen-space between clicked point and first working point
      // Click point we can get from event but first working point we need to project to screen space
      const ev = me || te
      let isScreenSpaceClose = false
      if (workingPoints.length > 2 && ev) {
        const screenPoint = me
          ? { x: me.clientX, y: me.clientY }
          : { x: te!.changedTouches[0].clientX, y: te!.changedTouches[0].clientY }

        const canvasRect = (ev.target as HTMLDivElement).getBoundingClientRect()
        const closingPointNDC = new Vector3(...workingPoints[0]).project(cameraRef.current!)
        const closingPointScreen = {
          x: ((closingPointNDC.x + 1) / 2) * canvasRect.width + canvasRect.left,
          y: ((-closingPointNDC.y + 1) / 2) * canvasRect.height + canvasRect.top,
        }
        const distanceToFirstPoint = Math.hypot(
          screenPoint.x - closingPointScreen.x,
          screenPoint.y - closingPointScreen.y,
        )
        isScreenSpaceClose = distanceToFirstPoint < EDITOR_DOUBLE_CLICK_POS_THRESHOLD
      }

      // It's closing if:
      // 1. user is in closing mode
      // 2. working points are more than 2 and user double clicked
      const completesPolygon = isClosing || isScreenSpaceClose || (workingPoints.length > 2 && isDoubleClicked)

      if (completesPolygon) {
        // Check for line intersections in 2D (since points are coplanar)
        if (!validateIntersectingLines(workingPoints)) {
          return
        }

        dispatch(completePlane({ isDoubleClicked }))
        setIsClosing(false)
        setPointProjectionPlane(null)
        // if the user double clicks with less than 3 points, ignore second click
      } else if (!isDoubleClicked) {
        dispatch(addWorkingPoint(clickedPoint))

        // If there are 3 points (2 from working, 1 currently added), set the plane for future use
        if (workingPoints.length === 2) {
          const pointsForPlane = workingPoints.concat([clickedPoint]).map((pt) => new Vector3(...pt))
          setPointProjectionPlane(
            new Plane().setFromCoplanarPoints(pointsForPlane[0], pointsForPlane[1], pointsForPlane[2]),
          )
        }
      }
    },
    [
      cameraRef,
      planes,
      workingPoints,
      isClosing,
      isSelectedTool,
      validateIntersectingLines,
      setIsClosing,
      setPointProjectionPlane,
      dispatch,
    ],
  )

  /**
   * When user completes the working plane.
   */
  const onComplete = useCallback(() => {
    if (!isSelectedTool || workingPoints.length < 2) return

    // Check for line intersections in 2D (since points are coplanar)
    if (!validateIntersectingLines(workingPoints)) {
      return
    }

    dispatch(completePlane({ isDoubleClicked: false }))
    setIsClosing(false)
    setPointProjectionPlane(null)
  }, [workingPoints, isSelectedTool, dispatch, validateIntersectingLines, setIsClosing, setPointProjectionPlane])

  /**
   * When user undoes the last working point.
   */
  const onUndo = useCallback(() => {
    if (!isSelectedTool) return

    dispatch(undoLastWorkingPlane())
  }, [isSelectedTool, dispatch])

  /**
   * When user cancels the working plane.
   */
  const onCancel = useCallback(() => {
    if (!isSelectedTool) return

    dispatch(resetWorkingPoints())
    setIsClosing(false)
    setPointProjectionPlane(null)
  }, [isSelectedTool, dispatch, setIsClosing, setPointProjectionPlane])

  /**
   * Focus on a plane.
   *
   * @param hashedItemId Hashed ID of the inspection item to focus on.
   */
  const onFocusItem = useCallback(
    async (hashedItemId: string) => {
      try {
        // Get the inspection item and make sure it's a plane item
        const item = planeItems.find((it) => hashString(it.inspection_item_id!) === hashedItemId)
        if (!item) return // no need to throw error, it can be a valid call from URL deep-link broadcasted to all tools

        // Get the shape associated with the inspection item
        const shape = shapes?.polygons?.find((polygon) => polygon.shape_id === item.shape_ids.polygons?.[0])
        if (!shape) throw new Error('Shape not found for the inspection item')

        await focusToItem(shape.positions, { customProfile: item.camera_profile || undefined })
      } catch (error) {
        Sentry.captureException(error, { tags: { location: 'PlaneDetection/useMainCanvas/onFocusItem' } })
      }
    },
    [planeItems, shapes, focusToItem],
  )

  /**
   * When user removes a plane, remove the toast if available.
   */
  useEffect(() => {
    if (planes.length < MAXIMUM_NUMBER_OF_SHAPE_DETECTION_POLYGON && toastIdRef.current) {
      toaster.dismiss(toastIdRef.current)
      toastIdRef.current = undefined
    }
  }, [planes.length])

  /**
   * Generate polygon mesh definition for saved planes.
   */
  const savedPlanes = useMemo(
    () =>
      (shapes?.polygons || [])
        .filter(
          (polygon) =>
            (!hiddenElementIds.includes(polygon.shape_id) ||
              isElementHovered(polygon, hoveredElementId, inspectionGroups)) &&
            !getVolumeEstimationItem(inspectionItems.items, polygon.shape_id),
        )
        .map<PolygonPlaneMeshProps>((polygon, index) => {
          const inspectionItem = inspectionItems.items.find(
            (item) =>
              item.shape_ids.polygons?.includes(polygon.shape_id) && item.item_type === InspectionItemType.PolygonArea,
          )

          const group = inspectionGroups.find((grp) => grp && polygon.inspection_group_id === grp.group_id)
          const isHovered = isElementHovered(polygon, hoveredElementId, inspectionGroups)
          const isSelected =
            selectedElementIds.includes(polygon.shape_id) ||
            selectedElementIds.includes(inspectionItem?.inspection_item_id || '')
          const ids = parseShapePartitionKey(polygon.partition_key || '')
          const parentArea = connectedAreas?.find(
            (area) => area.current.area.inspection_area_id === ids.inspectionAreaId,
          )

          let styles: Partial<PolygonPlaneMeshProps> = cloneDeep(DEFAULT_PLANES_STYLES)

          if (isHovered) {
            styles = cloneDeep(HOVERED_PLANE_STYLES)
          } else if (isSelected) {
            styles = cloneDeep(SELECTED_PLANE_STYLES)
          }

          // If guide line is disabled in settings, remove it
          if (!planeDisplaySettings.edge.label.visible) {
            delete styles.guideLabel
          }

          // If there's going to guide label, customize the styles a bit
          if (styles.guideLabel) {
            styles.guideLabel = {
              ...styles.guideLabel,
              styles: {
                backgroundColor: planeDisplaySettings.edge.label.bg_color || styles.guideLabel.styles?.backgroundColor,
                color: planeDisplaySettings.edge.label.text_color || styles.guideLabel.styles?.color,
              },
              isAdjustingColors:
                isAdjustingLabelColors &&
                currentAdjustingKey !== undefined &&
                [`plane.edge.label.text_color`, `plane.edge.label.bg_color`].includes(currentAdjustingKey),
            }
          }

          return {
            ...styles,
            id: polygon.shape_id,
            polygon,
            offsetTransformation: parentArea?.matrix,
            label:
              inspectionItem && showLabels && planeDisplaySettings.primary.label.visible
                ? {
                    ...styles.label,
                    visible: isHovered || isSelected,
                    label: `${
                      inspectionItem.part_name || `面積${zeroPad(index + 1, 3)}`
                    } : ${inspectionItem.polygon_area?.estimated_value?.toFixed(4)}m²`,
                    styles: {
                      ...styles.label?.styles,
                      color: planeDisplaySettings.primary.label.text_color,
                      backgroundColor: planeDisplaySettings.primary.label.bg_color,
                    },
                    isAdjustingColors:
                      isAdjustingLabelColors &&
                      currentAdjustingKey !== undefined &&
                      ['plane.primary.label.text_color', 'plane.primary.label.bg_color'].includes(currentAdjustingKey),
                  }
                : undefined,
            color: isGroupColoring
              ? group?.color || getUnlabeledColor(ids.inspectionAreaId)
              : planeDisplaySettings.color,
            opacity: planeDisplaySettings.opacity,
            isOpenLoop: true,
            selectable: isSelectorTool,
            guideLabel: showLabels ? styles.guideLabel : undefined,
            isAdjustingColors:
              (isAdjustingUnlabeledColors && isGroupColoring) ||
              (!isGroupColoring && currentAdjustingKey !== undefined && [`plane.color`].includes(currentAdjustingKey)),
          }
        }),
    [
      isSelectorTool,
      currentAdjustingKey,
      isGroupColoring,
      isAdjustingUnlabeledColors,
      isAdjustingLabelColors,
      planeDisplaySettings,
      getUnlabeledColor,
      showLabels,
      shapes,
      inspectionItems,
      inspectionGroups,
      selectedElementIds,
      hoveredElementId,
      hiddenElementIds,
      connectedAreas,
    ],
  )

  return {
    objects: {
      circleAnchors: anchors,
      polygonPlaneMeshesV2: [
        /**
         * Saved planes
         */
        ...savedPlanes,

        /**
         * Completed planes
         */
        ...useMemo(
          () =>
            planes.map<PolygonPlaneMeshProps>((plane, planeIndex) => {
              const vertices = plane.points.map((v) => [v[0], v[1]] as [number, number])
              const isHovered = isElementHovered(plane, hoveredElementId, inspectionGroups)

              return {
                ...getNonSavedPlaneStyles(plane, isHovered, false, selectedElementIds, hiddenElementIds),
                id: plane.id,
                polygon: {
                  shape_id: plane.id,
                  positions: plane.points,
                  vertices,
                  center: findCenter(plane.points.map((v) => new Vector3(...v)))?.toArray(),
                  transformation: [1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1],
                  min_rect_boundaries: generateDummyMinimumRectBoundary(vertices),
                } as Polygon,
                color: isGroupColoring ? 'white' : planeDisplaySettings.color,
                opacity: planeDisplaySettings.opacity,
                guideLabel: {
                  color: planeDisplaySettings.edge.label.text_color,
                  backgroundColor: planeDisplaySettings.edge.label.bg_color,
                },
                guideLine: {
                  color: '#000000',
                  style: LineStyle.Dashed,
                  renderOrder: 999,
                  thickness: 0.001,
                },
                vertexAnchors: {
                  depthTest: false,
                  depthWrite: false,
                  icon: CircleAnchorIcon.MOVE,
                  iconColor: 'white',
                  color: AnchorColors.Movable,
                  outlineOpacity: 0,
                  iconScale: 1.4,
                  scale: 1.3,
                  hoverScale: 1.5,
                  renderOrder: 1000,
                  onDown() {
                    // Keep constraining to the plane while editing
                    setPointProjectionPlane(threePlanesRef.current[plane.id])
                  },
                  onUpdated(points) {
                    // Check for line intersections in 2D (since points are coplanar)
                    if (!validateIntersectingLines(points)) {
                      // restore previous points
                      dispatch(updatePlanePoints({ planeIndex, points: plane.points }))
                      return
                    }

                    dispatch(updatePlanePoints({ planeIndex, points }))
                    setPointProjectionPlane(null)
                  },
                },
                isOpenLoop: true,
                selectable: false,
                isEditing: isEditing(plane.id),
              }
            }),
          [
            hiddenElementIds,
            hoveredElementId,
            selectedElementIds,
            planes,
            inspectionGroups,
            isGroupColoring,
            planeDisplaySettings,
            setPointProjectionPlane,
            validateIntersectingLines,
            isEditing,
            dispatch,
          ],
        ),

        /**
         * Saving planes
         */
        ...useMemo(
          () =>
            savingPlanes.map<PolygonPlaneMeshProps>((plane, planeIndex) => {
              const vertices = plane.points.map((v) => [v[0], v[1]] as [number, number])
              const isHovered = isElementHovered(plane, hoveredElementId, inspectionGroups)

              return {
                ...getNonSavedPlaneStyles(plane, isHovered, true, selectedElementIds, hiddenElementIds),
                id: `saving-plane-${planeIndex}`,
                color: isGroupColoring ? 'white' : planeDisplaySettings.color,
                opacity: planeDisplaySettings.opacity,
                polygon: {
                  shape_id: `saving-plane-${planeIndex}`,
                  positions: plane.points,
                  vertices,
                  center: findCenter(plane.points.map((v) => new Vector3(...v)))?.toArray(),
                  transformation: [1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1],
                  min_rect_boundaries: generateDummyMinimumRectBoundary(vertices),
                } as Polygon,
                selectable: false,
              }
            }),
          [
            hiddenElementIds,
            hoveredElementId,
            selectedElementIds,
            savingPlanes,
            inspectionGroups,
            isGroupColoring,
            planeDisplaySettings,
          ],
        ),

        /**
         * Error planes
         */
        ...useMemo(
          () =>
            errorPlanes.map<PolygonPlaneMeshProps>((plane, planeIndex) => {
              const vertices = plane.points.map((v) => [v[0], v[1]] as [number, number])
              const isHovered = isElementHovered(plane, hoveredElementId, inspectionGroups)

              return {
                ...getNonSavedPlaneStyles(plane, isHovered, false, selectedElementIds, hiddenElementIds),
                id: `error-plane-${planeIndex}`,
                polygon: {
                  shape_id: `error-plane-${planeIndex}`,
                  positions: plane.points,
                  vertices,
                  center: findCenter(plane.points.map((v) => new Vector3(...v)))?.toArray(),
                  transformation: [1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1],
                  min_rect_boundaries: generateDummyMinimumRectBoundary(vertices),
                } as Polygon,
                selectable: false,
              }
            }),
          [hiddenElementIds, hoveredElementId, selectedElementIds, errorPlanes, inspectionGroups],
        ),

        /**
         * Working plane
         */
        ...useMemo((): PolygonPlaneMeshProps[] => {
          if (!workingPoints.length) return []

          const pointsV3 = workingPoints.map((v) => new Vector3(...v))
          const { points: verticesV3, inverseMatrix } = alignToZAxis(pointsV3)
          const vertices = verticesV3.map((v) => [v.x, v.y] as [number, number])

          const polygons: PolygonPlaneMeshProps[] = [
            {
              ...WORKING_STYLES,
              id: 'working-plane-drawing',
              polygon: {
                shape_id: 'working-plane-drawing',
                positions: workingPoints,
                vertices,
                center: findCenter(pointsV3)?.toArray(),
                transformation: inverseMatrix.clone().invert().toArray(),
                min_rect_boundaries: generateDummyMinimumRectBoundary(vertices),
              } as Polygon,
              color: isGroupColoring ? 'white' : planeDisplaySettings.color,
              opacity: planeDisplaySettings.opacity,
              guideLine: {
                color: isClosing ? '#ff0000' : '#ffffff',
                style: LineStyle.Dashed,
                renderOrder: 999,
                thickness: 0.001,
              },
              guideLabel: {
                color: planeDisplaySettings.edge.label.text_color,
                backgroundColor: planeDisplaySettings.edge.label.bg_color,
                hideLastLabel: isClosing,
              },
              selectable: false,
              isOpenLoop: true,
              isWorking: true,
            },
          ]

          return polygons
        }, [workingPoints, isClosing, isGroupColoring, planeDisplaySettings]),
      ],
    },
    events: {
      onTouchStart(ev, point, solidMeshPoint) {
        touchedPoint.current = point || solidMeshPoint
      },
      onTouchEnd(ev, extras) {
        handleUp(null, ev, touchedPoint.current, touchedPoint.current, extras)
        isUpTouchedHandled.current = true
        touchedPoint.current = undefined
      },
      onMouseUp(ev, point, solidMeshPoint, extras) {
        if (isUpTouchedHandled.current) {
          isUpTouchedHandled.current = false
          return
        }

        handleUp(ev, null, point, solidMeshPoint, extras)
      },
      onUndo,
      onCancel,
      onComplete,
      onFocusItem,
    },
  }
}
